import { useFormularioTocado, useSubpantalla } from '../ui/navegacionReact';
import { useCallback, useEffect, useState } from 'react';
import { FileSignature, Loader2, Plus, Send, ShoppingCart, X } from 'lucide-react';
import { api } from '../api';
import { useAvisos } from '../lib/contextoAvisos';
import Ayuda from '../ui/Ayuda';
import NotaExplicativa from '../ui/NotaExplicativa';

/* =====================================================================
   Cotizaciones · ERP-OPERATIVO EC2
   =====================================================================
   Borrador → Enviada (congelada) → Aceptada/Rechazada, o Convertida en
   pedido sin recapturar. «Vencida» se calcula de la fecha.
   ===================================================================== */
function pesos(v) {
  const n = Number(v || 0);
  return `$ ${n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`;
}

export default function Cotizaciones() {
  const avisos = useAvisos();
  const [tick, setTick] = useState(0);
  const recargar = useCallback(() => setTick((t) => t + 1), []);
  const [lista, setLista] = useState(null);
  const [prospectos, setProspectos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [error, setError] = useState('');
  const [nueva, setNueva] = useState(false);
  useSubpantalla('nueva', nueva, () => setNueva(false), { reemplaza: true });
  const [abierta, setAbierta] = useState(null);
  useSubpantalla('cotizacion', !!abierta, () => setAbierta(null), { valor: abierta, abrir: setAbierta, reemplaza: true });

  useEffect(() => {
    let vivo = true;
    Promise.all([
      api.get('/comercial/cotizaciones'),
      api.get('/comercial/pipeline/prospectos').catch(() => []),
      api.get('/clientes').catch(() => []),
      api.get('/sucursales').catch(() => []),
    ]).then(([l, p, c, s]) => { if (vivo) { setLista(l); setProspectos(p); setClientes(Array.isArray(c) ? c : []); setSucursales(s); setError(''); } })
      .catch((e) => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, [tick]);

  async function accion(fn, exito) {
    try { const r = await fn(); avisos.exito(r.mensaje || exito); recargar(); }
    catch (e) { avisos.error(e.message); }
  }

  if (error && !lista) return <p className="p-4 text-sm text-red-700">{error}</p>;
  if (!lista) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Leyendo cotizaciones…
      </p>
    );
  }

  return (
    <div className="flex max-w-5xl flex-col gap-5 p-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Cotizaciones</h1>
        <p className="text-gray-600">Lo que prometiste a quién, hasta cuándo, y qué pasó con eso.</p>
        <Ayuda id="comercial.cotizaciones.pantalla" />
      </header>

      <button type="button" onClick={() => setNueva((v) => !v)}
              className="inline-flex w-fit items-center gap-2 rounded-xl bg-[var(--color-marca)] px-4 py-2 font-semibold text-white">
        <Plus className="h-4 w-4" aria-hidden="true" /> Nueva cotización
      </button>
      {nueva && <FormularioCotizacion prospectos={prospectos} clientes={clientes}
                                      onHecho={(m) => { avisos.exito(m); setNueva(false); recargar(); }} />}

      {lista.length === 0 ? (
        <NotaExplicativa titulo="Todavía no hay cotizaciones">
          Arma la primera: eliges a quién, hasta cuándo vale y qué le ofreces. Al enviarla queda congelada; al convertirla, el pedido se cobra en el mostrador.
        </NotaExplicativa>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
          {lista.map((c) => (
            <li key={c.id} className="px-3 py-2 text-sm">
              <div className="flex flex-wrap items-center gap-x-3">
                <FileSignature className="h-4 w-4 text-gray-400" aria-hidden="true" />
                <span className="font-mono text-gray-500">#{c.folio}</span>
                <span className="font-medium text-gray-900">{c.para}</span>
                <span className="text-gray-500">vale hasta {c.vigente_hasta}</span>
                <span className={`rounded px-2 text-xs ${c.estado_visible === 'VENCIDA' ? 'bg-red-50 text-red-800' : c.estado === 'CONVERTIDA' ? 'bg-green-50 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                  {c.estado_visible}
                </span>
                <span className="ml-auto tabular-nums font-semibold">{pesos(c.total)}</span>
                <button type="button" onClick={() => setAbierta(abierta === c.id ? null : c.id)}
                        className="font-semibold text-[var(--color-marca)]">Ver</button>
              </div>
              {abierta === c.id && (
                <Detalle id={c.id} sucursales={sucursales} recargar={recargar} accion={accion} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Detalle({ id, sucursales, accion }) {
  const [c, setC] = useState(null);
  const [suc, setSuc] = useState(sucursales[0]?.id || '');
  useEffect(() => {
    let vivo = true;
    api.get(`/comercial/cotizaciones/${id}`).then((d) => { if (vivo) setC(d); }).catch(() => {});
    return () => { vivo = false; };
  }, [id]);
  if (!c) return null;
  return (
    <div className="mt-2 rounded-lg bg-gray-50 p-3">
      <ul className="text-sm text-gray-700">
        {c.renglones.map((r) => (
          <li key={r.id} className="flex gap-3">
            <span className="flex-1">{r.descripcion}</span>
            <span>{Number(r.cantidad)} × {pesos(r.precio_unitario)}</span>
            <span className="w-24 text-right tabular-nums">{pesos(r.importe)}</span>
          </li>
        ))}
      </ul>
      <p className="mt-1 text-sm">Subtotal {pesos(c.subtotal)} · IVA {pesos(c.iva)} · <strong>Total {pesos(c.total)}</strong></p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {c.estado === 'BORRADOR' && (
          <button type="button" onClick={() => accion(() => api.post(`/comercial/cotizaciones/${id}/estado`, { a: 'ENVIADA' }))}
                  className="inline-flex items-center gap-1 rounded-xl bg-[var(--color-marca)] px-3 py-1.5 text-sm font-semibold text-white">
            <Send className="h-3 w-3" aria-hidden="true" /> Enviar
          </button>
        )}
        {(c.estado === 'ENVIADA' || c.estado === 'ACEPTADA') && !c.vencida && (
          <>
            {c.estado === 'ENVIADA' && (
              <button type="button" onClick={() => accion(() => api.post(`/comercial/cotizaciones/${id}/estado`, { a: 'ACEPTADA' }))}
                      className="rounded-xl border border-gray-300 px-3 py-1.5 text-sm font-semibold">Aceptada</button>
            )}
            <select value={suc} onChange={(e) => setSuc(e.target.value)} className="rounded-lg border border-gray-300 p-1.5 text-sm">
              {sucursales.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
            </select>
            <button type="button" disabled={!suc}
                    onClick={() => accion(() => api.post(`/comercial/cotizaciones/${id}/convertir`, { id_sucursal: Number(suc) }))}
                    className="inline-flex items-center gap-1 rounded-xl bg-[var(--color-marca)] px-3 py-1.5 text-sm font-semibold text-white disabled:bg-gray-300">
              <ShoppingCart className="h-3 w-3" aria-hidden="true" /> Convertir en pedido
            </button>
          </>
        )}
        {['BORRADOR', 'ENVIADA', 'ACEPTADA'].includes(c.estado) && (
          <button type="button" onClick={() => accion(() => api.post(`/comercial/cotizaciones/${id}/estado`, { a: 'RECHAZADA' }))}
                  className="inline-flex items-center gap-1 text-sm text-gray-600">
            <X className="h-3 w-3" aria-hidden="true" /> Rechazada
          </button>
        )}
        {c.estado === 'CONVERTIDA' && <span className="text-sm text-green-800">Pedido {c.folio_pedido}: se cobra en el mostrador con ese folio.</span>}
      </div>
      <Ayuda id="comercial.cotizaciones.enviada" />
      <Ayuda id="comercial.cotizaciones.convertir" />
    </div>
  );
}

function FormularioCotizacion({ prospectos, clientes, onHecho }) {
  const hoy = new Date();
  const en15 = new Date(hoy.getTime() + 15 * 86400000).toISOString().slice(0, 10);
  const [para, setPara] = useState(prospectos[0] ? `p:${prospectos[0].id}` : (clientes[0] ? `c:${clientes[0].id}` : ''));
  const [vigente, setVigente] = useState(en15);
  const [renglones, setRenglones] = useState([{ sku: '', descripcion: '', cantidad: '1', precio_unitario: '' }]);
  const [error, setError] = useState('');
  // Cotización a medio capturar (TC-DR-AT): atrás pregunta antes de cerrarla.
  const [vigilar] = useFormularioTocado({ sub: 'nueva' });
  const listo = para && renglones.every((r) => (r.sku.trim() || r.descripcion.trim()) && Number(r.cantidad) > 0);
  async function guardar(e) {
    e.preventDefault();
    const [tipo, id] = para.split(':');
    try {
      const r = await api.post('/comercial/cotizaciones', {
        id_prospecto: tipo === 'p' ? Number(id) : null, id_cliente: tipo === 'c' ? Number(id) : null,
        vigente_hasta: vigente,
        renglones: renglones.map((x) => ({ sku: x.sku.trim() || null, descripcion: x.descripcion.trim() || null,
                                          cantidad: Number(x.cantidad), precio_unitario: x.precio_unitario === '' ? null : Number(x.precio_unitario).toFixed(2) })),
      });
      onHecho(r.mensaje);
    } catch (err) { setError(err.message); }
  }
  const cambiar = (i, campo, v) => setRenglones(renglones.map((r, j) => (j === i ? { ...r, [campo]: v } : r)));
  return (
    <form ref={vigilar} onSubmit={guardar} className="flex flex-col gap-3 rounded-xl border border-gray-200 p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col text-sm">Para *
          <select value={para} onChange={(e) => setPara(e.target.value)} className="rounded-lg border border-gray-300 p-2">
            {prospectos.map((p) => <option key={`p:${p.id}`} value={`p:${p.id}`}>Prospecto · {p.empresa || p.nombre}</option>)}
            {clientes.map((c) => <option key={`c:${c.id}`} value={`c:${c.id}`}>Cliente · {c.nombre}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-sm">Vale hasta *
          <input type="date" value={vigente} onChange={(e) => setVigente(e.target.value)} className="rounded-lg border border-gray-300 p-2" />
        </label>
      </div>
      {renglones.map((r, i) => (
        <div key={i} className="grid gap-2 sm:grid-cols-4">
          <input value={r.sku} onChange={(e) => cambiar(i, 'sku', e.target.value)} placeholder="SKU (o deja vacío)" className="rounded-lg border border-gray-300 p-2 text-sm" />
          <input value={r.descripcion} onChange={(e) => cambiar(i, 'descripcion', e.target.value)} placeholder="Descripción" className="rounded-lg border border-gray-300 p-2 text-sm" />
          <input type="number" step="0.001" min="0" value={r.cantidad} onChange={(e) => cambiar(i, 'cantidad', e.target.value)} placeholder="Cantidad" className="rounded-lg border border-gray-300 p-2 text-sm" />
          <input type="number" step="0.01" min="0" value={r.precio_unitario} onChange={(e) => cambiar(i, 'precio_unitario', e.target.value)} placeholder="Precio (vacío = el del catálogo)" className="rounded-lg border border-gray-300 p-2 text-sm" />
        </div>
      ))}
      <button type="button" onClick={() => setRenglones([...renglones, { sku: '', descripcion: '', cantidad: '1', precio_unitario: '' }])}
              className="w-fit text-sm font-semibold text-[var(--color-marca)]">+ Renglón</button>
      {error && <p className="text-sm text-red-700">{error}</p>}
      <div className="flex items-center gap-3">
        <button type="submit" disabled={!listo} className="rounded-xl bg-[var(--color-marca)] px-4 py-2 font-semibold text-white disabled:bg-gray-300">Guardar borrador</button>
        <span className="text-xs text-gray-600">Cada renglón lleva SKU o descripción y una cantidad mayor que cero. Un renglón sin SKU se cotiza, pero para convertirla en pedido tiene que ser un producto del catálogo.</span>
      </div>
    </form>
  );
}
